import { Field } from '@flywire/headlessui-tailwindcss';
import { VisuallyHidden } from 'components/ui/VisuallyHidden/VisuallyHidden';
import type { InputProps } from 'hooks/useInputAriaProps/useInput';
import { useInput } from 'hooks/useInputAriaProps/useInput';
import { useInputAriaProps } from 'hooks/useInputAriaProps/useInputAriaProps';
import type { FunctionComponent, RefObject } from 'react';
import { useTranslations } from 'utils/translations/useTranslations';

import '../Input.scss';
import { ExclamationIcon } from '@flywire/icons';

const TextInput: FunctionComponent<InputProps> = ({
  'aria-describedby': ariaDescribedBy,
  className,
  disabled = false,
  error,
  floatingLabel = true,
  forwardRef,
  hint = '',
  label,
  name,
  id = name,
  onBlur,
  onChange,
  onFocus,
  type = 'text',
  readOnly = false,
  required = false,
  value = '',
  ...props
}) => {
  const { fieldValue, handleBlur, handleChange, handleFocus } = useInput({
    disabled,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    readOnly,
    value,
  });

  const { inputAriaProps } = useInputAriaProps({
    'aria-describedby': ariaDescribedBy,
    disabled,
    error,
    hint,
    label,
    name,
    readOnly,
    required,
  });
  const i18n = useTranslations();

  return (
    <Field id={id} floatingLabel={floatingLabel} className={className}>
      <div className="relative">
        <Field.Input
          data-testid={id}
          disabled={disabled}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          readOnly={readOnly}
          type={type}
          value={fieldValue}
          placeholder={label}
          {...props}
          {...inputAriaProps}
          ref={forwardRef as RefObject<HTMLInputElement>}
        />
        {label && (
          <Field.Label id={`${id}-label`}>
            <>{label}</>
            {required && <span aria-hidden> *</span>}
          </Field.Label>
        )}
        {error && type !== 'password' && (
          <div className="absolute top-3 ltr:right-3 rtl:left-3">
            <ExclamationIcon className="h-4 w-4 text-red-600" />
          </div>
        )}
      </div>
      {hint && (
        <Field.Hint className="mb-0" data-testid={`${name}-hint`}>
          {hint}
        </Field.Hint>
      )}
      {error && typeof error === 'string' && (
        <Field.Error className="mb-0" data-testid={`${name}-error`}>
          <VisuallyHidden as="span">{label}</VisuallyHidden>
          {i18n.t(error)}
        </Field.Error>
      )}
    </Field>
  );
};

export { TextInput };
